import { DestroyRef, Injectable, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { REALTIME_EVENTS } from '../hubs/realtime/config/realtime-events.config';
import {
  REALTIME_NOTIFICATION_SOURCE,
  type NotificationPayload,
} from '../hubs/realtime/models/notification.model';
import {
  REALTIME_ORDER_HUB_STATUS,
  type OrderRealtimePayload,
} from '../hubs/realtime/models/order-realtime.model';
import { RealtimeHubService } from '../hubs/realtime/realtime-hub.service';
import { I18nService } from '../i18n/i18n.service';
import { NotificationDisplayService } from '../notifications/notification-display.service';
import { NotificationSoundService } from '../notifications/notification-sound.service';
import {
  DESKTOP_NS,
  MENU_CATALOG_BATCH_SOURCES,
  MENU_CATALOG_SOURCE_LABEL,
  extractBatchId,
  formatOrderLabel,
  interpolateOrderBody,
  resolveOrderNotificationKeys,
} from '../notifications/order-notification-config';
import { ToasterService } from './toaster.service';
import { UserSessionService } from './user-session.service';

@Injectable({ providedIn: 'root' })
export class OrderRealtimeDesktopNotificationsService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly realtimeHub = inject(RealtimeHubService);
  private readonly session = inject(UserSessionService);
  private readonly i18n = inject(I18nService);
  private readonly toaster = inject(ToasterService);
  private readonly notificationSound = inject(NotificationSoundService);
  private readonly notificationDisplay = inject(NotificationDisplayService);

  private userGestureHandlersAttached = false;

  constructor() {
    this.realtimeHub
      .on$(REALTIME_EVENTS.OrderRealtime)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((payload) => this.onOrderHubEvent(payload));

    this.realtimeHub
      .on$(REALTIME_EVENTS.Notification)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((payload) => this.onGenericNotification(payload));

    effect(() => {
      const user = this.session.user();
      if (!user) return;
      this.attachOnUserGesture();
    });
  }

  /** Browsers require a user gesture before notification permission and audio playback. */
  private attachOnUserGesture(): void {
    if (this.userGestureHandlersAttached || typeof window === 'undefined') return;
    this.userGestureHandlersAttached = true;

    const onGesture = (): void => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
      if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
        void Notification.requestPermission();
      }
      this.notificationSound.warmup();
    };

    window.addEventListener('pointerdown', onGesture, { once: true, passive: true });
    window.addEventListener('keydown', onGesture, { once: true });
  }

  private showWithSound(title: string, body: string, tag: string): void {
    if (!this.session.user()) return;
    if (!this.notificationDisplay.canShow()) return;
    this.notificationSound.play();
    this.notificationDisplay.show(title, body, tag);
  }

  private onOrderHubEvent(payload: OrderRealtimePayload): void {
    if (!this.session.user()) return;
    if (!payload.id && !payload.orderNumber) return;

    const fallback = this.i18n.t(`${DESKTOP_NS}.fallbackOrderLabel`);
    const label = formatOrderLabel(payload, fallback);

    const statusCode = payload.status ?? REALTIME_ORDER_HUB_STATUS.Created;
    const keys = resolveOrderNotificationKeys(statusCode);

    const statusLabel = this.i18n.t(keys.statusKey);
    const title = this.i18n.t(keys.titleKey);
    const body = interpolateOrderBody(
      this.i18n.t(`${DESKTOP_NS}.bodyWithStatus`),
      label,
      statusLabel,
    );
    const tag = `order-status:${String(payload.id ?? label)}:${statusCode}`;

    this.showWithSound(title, body, tag);
  }

  private onGenericNotification(payload: NotificationPayload): void {
    if (!this.session.user()) return;

    const source = String(payload.source ?? '').trim();
    if (source === REALTIME_NOTIFICATION_SOURCE.DRIVER_ASSIGNMENT_UNAVAILABLE) {
      this.onDriverAssignmentUnavailableNotification(payload);
      return;
    }

    this.onMenuCatalogImportNotification(payload);
  }

  private onMenuCatalogImportNotification(payload: NotificationPayload): void {
    const source = String(payload.source ?? '').trim();
    if (!MENU_CATALOG_BATCH_SOURCES.has(source)) {
      return;
    }

    const title = String(payload.title ?? '').trim();
    const bodyBase = String(payload.message ?? '').trim();
    if (!title && !bodyBase) {
      return;
    }

    const sourceLabel = MENU_CATALOG_SOURCE_LABEL[source] ?? source;
    const body = bodyBase ? `${sourceLabel}\n${bodyBase}` : sourceLabel;

    const batchId = extractBatchId(payload.data);
    const tag = batchId
      ? `menu-catalog-import:${batchId}`
      : `menu-catalog-import:${source}:${payload.occurredAtUtc ?? Date.now()}`;

    this.showWithSound(title || body, body || title, tag);
  }

  private onDriverAssignmentUnavailableNotification(payload: NotificationPayload): void {
    const details = payload.driverAssignmentUnavailableData;
    if (!details) {
      return;
    }

    const sessionTenantId = (this.session.user()?.tenantId ?? '').trim();
    const eventTenantId = details.tenantId;
    if (sessionTenantId && eventTenantId && sessionTenantId !== eventTenantId) {
      return;
    }

    const orderNumber =
      details.orderNumber || this.i18n.t(`${DESKTOP_NS}.fallbackOrderLabel`);
    const message = String(payload.message ?? '').trim();
    if (!message) {
      return;
    }

    const orderId = details.orderId;
    const tag = orderId
      ? `driver-assignment-unavailable:${orderId}`
      : `driver-assignment-unavailable:${payload.occurredAtUtc ?? Date.now()}`;

    if (this.notificationDisplay.isUserActivelyViewingApp()) {
      this.toaster.showWarn(`${orderNumber}: ${message}`);
      this.notificationSound.play();
      return;
    }

    this.showWithSound(orderNumber, message, tag);
  }
}
